import type { CalculatedSemNode } from 'types/data/CalculatedSemNode';

/**
 * Represents a cell selection with user, column, and node information
 */
export type CellSelection = {
  userId: string;
  columnIndex: number;
  nodeId: string;
};

/**
 * Represents the cell selection data structure used in grid state
 */
export type CellSelectionRecord = {
  [userId: string]: { [resultSetId: string]: { [nodeMatrixColumnIndex: number]: number } };
};

/**
 * Stable cell reference using nodeId + columnKey
 */
export type CellReference = {
  userId: string;
  nodeId: string;
  columnKey: string;
};

/**
 * Captured cell selections by result set
 */
export type CapturedSelections = {
  [resultSetId: string]: {
    selectedCells: CellReference[];
    editCells: CellReference[];
  };
};

/**
 * Partial grid state interface containing only the properties needed for cell selection persistence
 */
export interface GridStateForCellSelection {
  selectedCells?: CellSelectionRecord;
  editCells?: CellSelectionRecord;
  gridSpecifications: {
    [resultSetId: string]: {
      nodeMatrixColumnIndexMap: { [columnIndex: number]: number };
      columnIndexByKey: { [columnKey: string]: number };
    };
  };
  nodeMatrix: {
    [resultSetId: string]: CalculatedSemNode[][];
  };
}

/**
 * Capture current cell selections before recalculation
 */
export function captureCurrentSelections(state: GridStateForCellSelection, resultSetIds: string[]): CapturedSelections {
  const capturedSelections: CapturedSelections = {};

  for (const resultSetId of resultSetIds) {
    capturedSelections[resultSetId] = { selectedCells: [], editCells: [] };

    const nodeMatrix = state.nodeMatrix[resultSetId];
    const gridSpec = state.gridSpecifications[resultSetId];

    if (nodeMatrix && gridSpec) {
      // Create reverse mapping for efficient column key lookup
      const columnKeyByIndex: { [columnIndex: number]: string } = {};
      Object.entries(gridSpec.columnIndexByKey).forEach(([key, index]) => {
        columnKeyByIndex[index] = key;
      });

      // Capture selected cells
      if (state.selectedCells) {
        Object.entries(state.selectedCells).forEach(([userId, userSelections]) => {
          if (userSelections[resultSetId]) {
            Object.entries(userSelections[resultSetId]).forEach(([columnIndexStr, rowIndex]) => {
              const columnIndex = Number(columnIndexStr);
              const dataColumnIndex = gridSpec.nodeMatrixColumnIndexMap[columnIndex];
              if (dataColumnIndex === undefined) return;

              const node = nodeMatrix[dataColumnIndex]?.[rowIndex];
              const columnKey = columnKeyByIndex[columnIndex];

              if (node && columnKey) {
                capturedSelections[resultSetId].selectedCells.push({
                  userId,
                  nodeId: node.id,
                  columnKey
                });
              }
            });
          }
        });
      }

      // Capture edit cells
      if (state.editCells) {
        Object.entries(state.editCells).forEach(([userId, userEdits]) => {
          if (userEdits[resultSetId]) {
            Object.entries(userEdits[resultSetId]).forEach(([columnIndexStr, rowIndex]) => {
              const columnIndex = Number(columnIndexStr);
              const dataColumnIndex = gridSpec.nodeMatrixColumnIndexMap[columnIndex];
              if (dataColumnIndex === undefined) return;

              const node = nodeMatrix[dataColumnIndex]?.[rowIndex];
              const columnKey = columnKeyByIndex[columnIndex];

              if (node && columnKey) {
                capturedSelections[resultSetId].editCells.push({
                  userId,
                  nodeId: node.id,
                  columnKey
                });
              }
            });
          }
        });
      }
    }
  }

  return capturedSelections;
}

/**
 * Restore captured cell selections after recalculation
 */
export function restoreCapturedSelections<T extends GridStateForCellSelection>(
  state: T,
  capturedSelections: CapturedSelections,
  resultSetIds: string[]
): T {
  // Create a new state object with restored selections
  const restoredSelections: CellSelectionRecord = { ...state.selectedCells };
  const restoredEditCells: CellSelectionRecord = { ...state.editCells };

  for (const resultSetId of resultSetIds) {
    const captures = capturedSelections[resultSetId];
    const nodeMatrix = state.nodeMatrix[resultSetId];
    const gridSpec = state.gridSpecifications[resultSetId];

    if (captures && nodeMatrix && gridSpec) {
      // Restore selected cells
      captures.selectedCells.forEach(({ userId, nodeId, columnKey }) => {
        const columnIndex = gridSpec.columnIndexByKey[columnKey];
        if (columnIndex !== undefined) {
          const dataColumnIndex = gridSpec.nodeMatrixColumnIndexMap[columnIndex];
          if (dataColumnIndex === undefined) return;

          const column = nodeMatrix[dataColumnIndex];
          if (column) {
            const rowIndex = column.findIndex((node) => node?.id === nodeId);
            if (rowIndex >= 0) {
              if (!restoredSelections[userId]) restoredSelections[userId] = {};
              if (!restoredSelections[userId][resultSetId]) restoredSelections[userId][resultSetId] = {};
              restoredSelections[userId][resultSetId][columnIndex] = rowIndex;
            }
          }
        }
      });

      // Restore edit cells
      captures.editCells.forEach(({ userId, nodeId, columnKey }) => {
        const columnIndex = gridSpec.columnIndexByKey[columnKey];
        if (columnIndex !== undefined) {
          const dataColumnIndex = gridSpec.nodeMatrixColumnIndexMap[columnIndex];
          if (dataColumnIndex === undefined) return;

          const column = nodeMatrix[dataColumnIndex];
          if (column) {
            const rowIndex = column.findIndex((node) => node?.id === nodeId);
            if (rowIndex >= 0) {
              if (!restoredEditCells[userId]) restoredEditCells[userId] = {};
              if (!restoredEditCells[userId][resultSetId]) restoredEditCells[userId][resultSetId] = {};
              restoredEditCells[userId][resultSetId][columnIndex] = rowIndex;
            }
          }
        }
      });
    }
  }

  return {
    ...state,
    selectedCells: restoredSelections,
    editCells: restoredEditCells
  };
}

/**
 * Convenience wrapper that captures selections, runs a recalculation function, then restores selections
 */
export async function withCellSelectionPersistence<T extends GridStateForCellSelection>(
  state: T,
  resultSetIds: string[],
  recalculationFn: (state: T) => Promise<T> | T
): Promise<T> {
  // Capture current selections
  const capturedSelections = captureCurrentSelections(state, resultSetIds);

  // Perform recalculation
  const updatedState = await recalculationFn(state);

  // Restore selections
  return restoreCapturedSelections(updatedState, capturedSelections, resultSetIds);
}
